// @givmo/board — what a director can read of their board's minute book, and acknowledging it.
//
// ══ THIS IS A READING SURFACE, AND THE BACKEND MADE IT ONE ═════════════════════════════════════
//
// The director's record model is a deliberately NARROW projection: id, kind, state, title, body, the
// date it is about, and its content fingerprint. No confidentiality flag, no provenance, no lineage
// pointers, no event log. So this surface renders a document to read — it is not a second, thinner
// copy of the officer's record browser, and it must not grow into one by inventing fields the
// projection does not carry.
//
// What a director sees is decided entirely by the backend for the seat their account holds on this
// board. Nothing here narrows it unasked, counts against a total, or reports a gap: a record withheld
// from this seat is simply not in the list, and that absence is the whole mechanism.
//
// ══ THE OFFICER'S MINUTE BOOK, AND A DRAFT NEVER READS AS THE RECORD (CBG-UX10, CBG-FIX140) ═══════
//
// The list is the officer's book (frame c4): "Minute book", its table (title, type, date, status)
// with its search and its two filters, the reader's own. A draft is never the record (plan §3.3), and
// a director must still read one (minutes approved by written consent, D-CBG-22), so drafts are
// listed apart under "In progress", each "Draft · not the record until your board approves it" with
// "Read the draft", and a draft's own page says "Draft" beside its kind and date, the officer
// record page's word, leads its text with that whole line, and says when its draft was started,
// never "Entered" (review-291's M1). The book's other rows say their standing in the officer book's
// words.
//
// ══ ACKNOWLEDGING IS SIGNATURE-CLASS ═══════════════════════════════════════════════════════════
//
// It runs through the shared sheet's gate like every act (`features/director/director-act.tsx`), and
// the act records the method, when it was confirmed and the sentence it affirmed. The content
// fingerprint is echoed because the act's whole value is that it names the exact bytes a human read:
// if the record moved under them the backend refuses, and the honest thing to show is that it changed
// — never a retry that acknowledges whatever it became. A record with no fingerprint is not the
// record yet, and there is nothing to acknowledge.
//
// ══ OPENING ONE CAN REFUSE TOO ═════════════════════════════════════════════════════════════════
//
// An executive session's record needs the signing window open, under both access modes — a floor
// beneath the setting. It answers the same typed refusal the acts do, so the read gate opens the same
// sheet and re-reads this record (`director/read-gate.tsx`). ⚠ THE LIST DOES NOT REFUSE — IT
// WITHHOLDS BY ABSENCE: a closed session this seat may not read is simply not in it, and inventing a
// "some records are hidden" line would announce exactly what the absence is designed not to.
//
// A SEAT WHOSE ACCOUNT CAN'T OPEN IT (CBG-SEC2-L4-MOVE; review-89's P1): on a board where another
// organization's sign-in lock keeps the account from executive-session material, a named closed
// session's record is refused (`board_another_account_required`) once the window is open. Trying
// again can never open it, so the page says why and points at the move instead of "Try again".
//
// ══ OPENING MINUTES ONE WASN'T SENT (CBG-RC12-READ, frames d2-open, d3-opened) ══════════════════
//
// A current director may OPEN a closed session's minutes they weren't sent, and the record read IS
// the opening, recorded in the minutes' history. So when the meeting page's "Open its minutes" leads
// here (`?meeting=&session=`, `closed-minutes-opening.tsx`), the page first shows the step, built
// from the session's public entry alone, and reads the record only once "Open the minutes" is
// pressed. Whatever way a read opened them, it answers `opened_at`, and the page leads with it.
//
// ══ SIGNING TO APPROVE A CLOSED SESSION'S MINUTES (CBG-RC10, frame d5) ══════════════════════════
//
// Where the board's rule approves a closed session's minutes by the signatures of the directors who
// were in it, the read answers `approval_request` for a director the open request asked (by custody,
// backend `board_minutes.md` §S5): how many have signed, and where this director's signature stands.
// The page leads with it, above the minutes, and Sign runs the act through the signing window like
// every act (`director-act.tsx`). The echo is the read's own `pending_content_hash`, the bytes this
// page rendered; if the minutes moved under the reader, nothing is signed and the page says so.

import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Fingerprint } from '../../components/fingerprint';
import { Link, useNavigate, useParams, useSearch } from '../../board-routing';
import {
  firstErrorCode,
  isGivmoError,
  type OrgBoardDirectorMeetingOut,
  type OrgBoardDirectorRecordOut,
} from '@givmo/api-client';
import {
  Button,
  Card,
  CardBody,
  DataTable,
  EmptyState,
  ErrorMessage,
  Icon,
  SearchInput,
  Select,
  Skeleton,
  testIdProp,
  type DataTableColumn,
} from '@givmo/design-system';
import { BookText } from 'lucide-react';
import { extractList } from '@givmo/api-client/list';
import { BOARD_DIRECTOR_PAGES } from '../../board-routes';
import { useBoardDirectorOperations } from '../../board-runtime';
import {
  directorBoardIdentity,
  directorBoardSearch,
  useDirectorBoardAddress,
} from '../../director/director-board';
import { boardDirectorMeKey } from '../../director/director-me';
import { DIRECTOR_REFUSALS, isCommitteeOnly } from '../../director/director-wire';
import { committeeOnlyWords as co } from '../committees/committee-only-words';
import {
  DirectorReadRefusal,
  isSigningRefusal,
  useDirectorReadsMayFire,
} from '../../director/read-gate';
import { DirectorActRow, DirectorSetupOfferPage, useDirectorAct } from './director-act';
import { AllYourBoards } from './all-your-boards';
import { DirectorBoardContent } from './director-board-content';
import { directorPagesWords as dp } from './director-pages-words';
import { DirectorReadFailed, useDirectorFailureWords } from '../../director/access-refusal';
import { DirectorShell } from './director-shell';
import { DirectorMovePointer } from './move-pointer';
import { closedMinutesWords as w } from './closed-minutes-words';
import {
  CLOSED_MINUTES_MEETING_KEY,
  CLOSED_MINUTES_SESSION_KEY,
  directorMeetingRecordKey,
  useClosedMinutesOpening,
} from './closed-minutes-opening';
import {
  closedMinutesWay,
  decodeDirectorMeetingRecord,
  steppedOutIsMine,
} from './director-minutes-decode';
import { minutesConsentWords as rc9 } from '../consents/minutes-consent-words';
import { minutesRuleWords as rc11 } from '../minutes/minutes-rule-words';
import { directorApprovalRequest } from '../minutes/minutes-signatures-decode';
import { minutesSignaturesWords as rc10 } from '../minutes/minutes-signatures-words';
import {
  recordKindLabel,
  recordKindLabelOf,
  recordStandingLabel,
  sentenceMoment,
} from '../records/records-decode';
import { GroupLabel, StatusWord } from '../../components/fine-print';
import { displayDate, displayInstant } from '../../lib/display';
import { shortDay } from '../conflicts/coi-decode';
// CBG-YR4-START, -ACK (frame g6): the way back to the getting-started list, and the read's own
// acknowledgment.
import { directorGettingStartedKey } from './director-getting-started-page';
import { onboardingWords as gsWords } from '../onboarding/onboarding-words';

/** A record still on its way to being the record (a draft, one awaiting approval, a discarded one). */
function notTheRecord(record: OrgBoardDirectorRecordOut): boolean {
  const state = (record.state ?? '').toLowerCase();
  return state !== 'approved' && state !== 'imported';
}

/** A search value the record page reads, or null. */
function searchText(search: Record<string, unknown>, key: string): string | null {
  const value = search[key];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

const BACK_LINK =
  'font-sans text-s14 font-w600 tracking-givmo text-brand-600 outline-none transition-colors hover:text-brand-700 focus-visible:underline';

/** CBG-SEC2-L4-MOVE: refused for another Givmo account, which no retry from this one changes. */
function isAnotherAccountRefusal(error: unknown): boolean {
  return isGivmoError(error) && firstErrorCode(error) === DIRECTOR_REFUSALS.anotherAccountRequired;
}

// ── The list ─────────────────────────────────────────────────────────────────────────────────────

/** The book's "no filter" value: a Select needs a string. */
const ANY = 'any';

export function DirectorRecordsPage() {
  const operations = useBoardDirectorOperations();
  const { orgRef, board, notYours, scope, me, settled } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);
  const [text, setText] = useState('');
  const [kind, setKind] = useState<string>(ANY);
  const [standing, setStanding] = useState<string>(ANY);

  const recordsQuery = useQuery({
    queryKey: ['board-director-records', orgRef],
    enabled: orgRef !== null && mayFire,
    retry: false,
    queryFn: async (): Promise<OrgBoardDirectorRecordOut[]> => {
      const { data } = await operations.directorListBoardDirectorRecords({
        path: { org_ref: orgRef ?? '' },
        query: { 'page[limit]': 100 },
        throwOnError: true,
      });
      // ⚠ Extracted AT THE DIRECTOR'S OWN TYPE, never through the officer's: reading this narrow
      // projection as the officer's record would make every withheld field look present and optional
      // to the compiler, and a surface here could reach for one and render `undefined`.
      return extractList<OrgBoardDirectorRecordOut>(data, 'data');
    },
  });

  const records = recordsQuery.data ?? [];
  // CBG-FIX140: a draft is never the record. It is listed apart, under "In progress", and says so;
  // everything else is the book's table, each row's standing in its own word.
  const drafts = records.filter((record) => record.state === 'draft');
  const book = records.filter((record) => record.state !== 'draft');
  const kinds = [...new Set(book.map((record) => record.record_kind))];
  // A director's record's standing in the officer book's own words (`recordStandingLabel`,
  // CBG-UX3): the read says whether a written consent's signatures filed it (`signed_by_everyone`,
  // CBG-FIX146), so one filed by its last signature reads "Signed by everyone" and an officer's
  // approval "Approved", as there.
  const standings = [...new Set(book.map((record) => recordStandingLabel(record)))];
  const needle = text.trim().toLowerCase();
  const narrowed = kind !== ANY || standing !== ANY || needle.length > 0;
  const shown = book.filter(
    (record) =>
      (kind === ANY || record.record_kind === kind) &&
      (standing === ANY || recordStandingLabel(record) === standing) &&
      (needle.length === 0 || record.title.toLowerCase().includes(needle)),
  );
  const search = orgRef === null ? undefined : directorBoardSearch(orgRef);
  // CBG-YR6-OUTSIDE (not drawn): a seat for someone who isn't on the board reads its committees'
  // records alone (the backend lists only those), named here as theirs — and still once a refusal
  // has taken the seat away (CBG-FIX104, row 4.1: its header beside the line).
  const outside = scope.committeeOnly;

  const columns: DataTableColumn<OrgBoardDirectorRecordOut>[] = [
    {
      id: 'title',
      header: 'Title',
      cell: (record) => (
        <Link
          to={BOARD_DIRECTOR_PAGES.record}
          params={{ recordId: record.id }}
          search={search}
          className="font-w600 text-text-strong outline-none hover:text-brand-700 focus-visible:underline"
          data-testid={`director-record-link-${record.id}`}
        >
          {record.title}
        </Link>
      ),
      stacked: 'heading',
    },
    {
      id: 'type',
      header: 'Type',
      cell: (record) => <span className="text-text-secondary">{recordKindLabelOf(record)}</span>,
      width: '176px',
    },
    {
      id: 'date',
      header: 'Date',
      cell: (record) => (
        <span className="text-text-secondary tabular-nums">
          {record.occurred_on ? displayDate(record.occurred_on) : '—'}
        </span>
      ),
      width: '136px',
    },
    {
      id: 'status',
      header: 'Status',
      cell: (record) => (
        <StatusWord testId={`director-record-status-${record.id}`}>
          {recordStandingLabel(record)}
        </StatusWord>
      ),
      width: '160px',
    },
  ];

  return (
    <DirectorShell
      title={outside ? co.recordsTitle : dp.minuteBook}
      description={outside ? co.recordsDescription : dp.bookLine}
      identity={directorBoardIdentity(board)}
      actions={
        <AllYourBoards
          me={me}
          settled={settled}
          committeeOnly={outside}
          testId="director-records-back"
        />
      }
      testId="page-director-records"
    >
      <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
        {recordsQuery.isPending ? (
          <Skeleton className="h-[240px] w-full rounded-xl" testId="director-records-loading" />
        ) : null}

        <DirectorReadRefusal reads={[recordsQuery]} testId="director-records-read-step-up" />

        {recordsQuery.isError && !isSigningRefusal(recordsQuery.error) ? (
          <DirectorReadFailed
            read={recordsQuery}
            line="We couldn’t load these just now."
            testId="director-records-error"
            retryTestId="director-records-retry"
          />
        ) : null}

        {drafts.length > 0 ? (
          <section className="flex flex-col gap-10" data-testid="director-records-in-progress">
            <GroupLabel>{dp.inProgress}</GroupLabel>
            <Card className="overflow-hidden">
              <ul className="divide-y divide-border-subtle">
                {drafts.map((record) => (
                  <li key={record.id} data-testid={`director-records-draft-${record.id}`}>
                    {/* The officer's in-progress row: on a phone the way to the draft follows the
                        lines; from `sm` it sits at the row's right. */}
                    <Link
                      to={BOARD_DIRECTOR_PAGES.record}
                      params={{ recordId: record.id }}
                      search={search}
                      className="group grid grid-cols-1 items-baseline gap-x-16 gap-y-4 px-20 py-14 outline-none transition-colors hover:bg-surface-hover focus-visible:bg-surface-hover sm:grid-cols-[minmax(0,1fr)_auto]"
                      data-testid={`director-records-draft-link-${record.id}`}
                    >
                      <span className="min-w-0 font-sans text-s15 font-w600 tracking-givmo text-text-strong transition-colors group-hover:text-brand-700">
                        {record.title}
                      </span>
                      <span className="font-sans text-s13 font-w600 tracking-givmo text-brand-700 max-sm:order-last sm:text-right">
                        {dp.readTheDraft}
                      </span>
                      <span
                        className="min-w-0 font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
                        data-testid={`director-records-draft-state-${record.id}`}
                      >
                        {recordStandingLabel(record)} · {dp.untilApproved}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          </section>
        ) : null}

        {book.length > 0 ? (
          <div
            className="flex flex-col gap-10 sm:flex-row sm:items-center"
            data-testid="director-records-toolbar"
          >
            <SearchInput
              placeholder="Search records"
              aria-label="Search the minute book by title"
              value={text}
              onChange={(event) => setText(event.target.value)}
              onClear={() => setText('')}
              containerClassName="w-full sm:w-[280px]"
              testId="director-records-search"
            />
            <div className="grid grid-cols-2 gap-10 sm:flex">
              <Select
                appearance="saas"
                aria-label="Type"
                options={[
                  { value: ANY, label: 'All types' },
                  ...kinds.map((value) => ({ value, label: recordKindLabel(value) })),
                ]}
                value={kind}
                onValueChange={(next) => setKind(next ?? ANY)}
                className="sm:w-[184px]"
                testId="director-records-filter-kind"
              />
              <Select
                appearance="saas"
                aria-label="Status"
                options={[
                  { value: ANY, label: 'Any status' },
                  ...standings.map((value) => ({ value, label: value })),
                ]}
                value={standing}
                onValueChange={(next) => setStanding(next ?? ANY)}
                className="sm:w-[184px]"
                testId="director-records-filter-state"
              />
            </div>
          </div>
        ) : null}

        {recordsQuery.isSuccess && records.length === 0 ? (
          <EmptyState
            testId="director-records-empty"
            icon={<Icon icon={BookText} size={20} aria-hidden />}
            title={dp.bookEmptyTitle}
            description={dp.bookEmptyLine}
          />
        ) : null}

        {recordsQuery.isSuccess && records.length > 0 && book.length === 0 ? (
          <p
            className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary"
            data-testid="director-records-nothing-approved"
          >
            {dp.nothingApprovedYet}
          </p>
        ) : null}

        {book.length > 0 && shown.length === 0 && narrowed ? (
          <p
            className="font-sans text-s14 font-w400 tracking-givmo text-text-secondary"
            data-testid="director-records-none-in-filter"
          >
            {dp.noMatch}
          </p>
        ) : null}

        {shown.length > 0 ? (
          <Card className="overflow-hidden" testId="director-records-table">
            <DataTable
              columns={columns}
              rows={shown}
              rowKey={(record) => record.id}
              rowTestId={(record) => `director-record-${record.id}`}
              stackAt="sm"
            />
          </Card>
        ) : null}
      </DirectorBoardContent>
    </DirectorShell>
  );
}

// ── One record ───────────────────────────────────────────────────────────────────────────────────

export function DirectorRecordDetailPage() {
  const { recordId } = useParams({ from: '/director/records/$recordId' });
  const queryClient = useQueryClient();
  const operations = useBoardDirectorOperations();
  const { orgRef, board, notYours, scope } = useDirectorBoardAddress();
  const mayFire = useDirectorReadsMayFire(orgRef);
  // CBG-FIX104: the access refusal in the words of the seat `/me` listed here.
  const failureWords = useDirectorFailureWords();
  const [error, setError] = useState<string | null>(null);
  const [staleText, setStaleText] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  // CBG-RC10: the approval signature's own error, and whether this director's was the last.
  const [approveError, setApproveError] = useState<string | null>(null);
  const [signedLast, setSignedLast] = useState(false);

  // CBG-RC12-READ: the step before opening minutes this director wasn't sent (see the header).
  const search = useSearch();
  const navigate = useNavigate();
  const stepMeetingId = searchText(search, CLOSED_MINUTES_MEETING_KEY);
  const stepSessionId = searchText(search, CLOSED_MINUTES_SESSION_KEY);
  const stepping = stepMeetingId !== null && stepSessionId !== null;
  const [openPressed, setOpenPressed] = useState(false);
  const opening = useClosedMinutesOpening(orgRef, stepMeetingId ?? '');
  const stepRecordQuery = useQuery({
    queryKey: directorMeetingRecordKey(orgRef, stepMeetingId),
    enabled: stepping && orgRef !== null && mayFire,
    retry: false,
    queryFn: async () => {
      const { data } = await operations.directorGetBoardDirectorMeetingRecord({
        path: { org_ref: orgRef ?? '', meeting_id: stepMeetingId ?? '' },
        throwOnError: true,
      });
      return decodeDirectorMeetingRecord(data);
    },
  });
  const stepMeetingQuery = useQuery({
    queryKey: ['board-director-meeting', orgRef, stepMeetingId],
    enabled: stepping && orgRef !== null && mayFire,
    retry: false,
    queryFn: async (): Promise<OrgBoardDirectorMeetingOut> => {
      const { data } = await operations.directorGetBoardDirectorMeeting({
        path: { org_ref: orgRef ?? '', meeting_id: stepMeetingId ?? '' },
        throwOnError: true,
      });
      return data;
    },
  });
  const stepEntry =
    stepRecordQuery.data?.sessionEntries?.find((entry) => entry.id === stepSessionId) ?? null;
  const stepWay = stepEntry === null ? null : closedMinutesWay(stepEntry);
  // `step`: minutes not sent to this director, not opened yet. `ask`: the window shut since the
  // meeting page — confirm first, as there. `read`: anything else reads the record as it always has.
  const phase: 'pending' | 'step' | 'ask' | 'read' =
    !stepping || openPressed
      ? 'read'
      : stepRecordQuery.isPending
        ? 'pending'
        : stepWay?.kind === 'open' && !stepWay.sentToYou && stepWay.recordId === recordId
          ? 'step'
          : stepWay?.kind === 'ask' && board?.another_account_required !== true
            ? 'ask'
            : 'read';

  const recordKey = ['board-director-record', orgRef, recordId] as const;
  const recordQuery = useQuery({
    queryKey: recordKey,
    // The read IS the opening of minutes this director wasn't sent: never before the step is taken.
    enabled: orgRef !== null && mayFire && phase === 'read',
    // A refusal is a deterministic typed 400, not a blip: retrying it just delays the sheet.
    retry: false,
    queryFn: async (): Promise<OrgBoardDirectorRecordOut> => {
      const { data } = await operations.directorGetBoardDirectorRecord({
        path: { org_ref: orgRef ?? '', record_id: recordId },
        throwOnError: true,
      });
      return data;
    },
  });
  const record = recordQuery.data ?? null;

  const acknowledge = useDirectorAct('record_acknowledgment', {
    onDone: async () => {
      setAcknowledged(true);
      await queryClient.invalidateQueries({ queryKey: recordKey });
      // CBG-YR4-START: the member's list and its attention row read this act.
      await queryClient.invalidateQueries({ queryKey: directorGettingStartedKey(orgRef) });
      await queryClient.invalidateQueries({ queryKey: ['board-director-attention', orgRef] });
    },
    onFailed: (caught) => {
      if (isGivmoError(caught) && firstErrorCode(caught) === DIRECTOR_REFUSALS.intentChanged) {
        setError('The words above changed. Read them, then try again.');
        void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
        return;
      }
      if (isGivmoError(caught) && firstErrorCode(caught) === 'board_record_already_acknowledged') {
        // Recorded already (a second tab, a double press): the act stands once, and says so.
        setAcknowledged(true);
        return;
      }
      if (isGivmoError(caught) && caught.status === 409) {
        // The record moved while this was open. Never acknowledge whatever it has become.
        setStaleText(true);
        setError(null);
        void queryClient.invalidateQueries({ queryKey: recordKey });
        return;
      }
      setError(failureWords(caught, 'We couldn’t record that. Please try again.'));
    },
  });

  const approveMinutes = useDirectorAct<{ data?: { approved?: boolean } }>(
    'minutes_approval_signature',
    {
      onDone: async (value) => {
        if (value?.data?.approved === true) setSignedLast(true);
        await queryClient.invalidateQueries({ queryKey: recordKey });
        await queryClient.invalidateQueries({ queryKey: ['board-director-attention', orgRef] });
      },
      onFailed: (caught) => {
        if (isGivmoError(caught) && firstErrorCode(caught) === DIRECTOR_REFUSALS.intentChanged) {
          setApproveError('The words above changed. Read them, then sign again.');
          void queryClient.invalidateQueries({ queryKey: boardDirectorMeKey() });
          return;
        }
        // The server's own sentence, as the minutes signature shows it: the minutes changed since
        // they were read, this seat already signed these bytes, the request was withdrawn or the
        // session's recorded seats changed — each needs something different, and its sentence
        // says which. The read is asked again, so the page shows where things now stand.
        setApproveError(failureWords(caught, 'We couldn’t record that. Please try again.'));
        void queryClient.invalidateQueries({ queryKey: recordKey });
      },
    },
  );

  const identity = directorBoardIdentity(board);
  // CBG-YR4-START (frame g6): opened from the getting-started list, the way back is the list, and the
  // board's ask is a fact. The act below is the shipped one, unchanged: "Record that I’ve read this".
  const fromList = search.from === 'getting-started';
  const back = fromList ? (
    <Link
      to={BOARD_DIRECTOR_PAGES.gettingStarted}
      search={orgRef === null ? undefined : directorBoardSearch(orgRef)}
      className={BACK_LINK}
      {...testIdProp('director-record-back')}
    >
      {gsWords.backToList}
    </Link>
  ) : (
    <Link
      to={BOARD_DIRECTOR_PAGES.records}
      search={orgRef === null ? undefined : directorBoardSearch(orgRef)}
      // Exact: this page sits beneath that address, and it is not the current page.
      exact
      className={BACK_LINK}
      {...testIdProp('director-record-back')}
    >
      {/* CBG-YR6-OUTSIDE (frame o11): a committee-only seat's records are its committees'. CBG-UX10:
          the board's, the officer record page's way back. */}
      {scope.committeeOnly ? co.allCommitteeRecords : dp.minuteBook}
    </Link>
  );

  if (acknowledge.offering || approveMinutes.offering) {
    return (
      <DirectorSetupOfferPage
        variant="from-sign"
        identity={identity}
        onSettled={approveMinutes.offering ? approveMinutes.settleOffer : acknowledge.settleOffer}
      />
    );
  }

  if (phase === 'step' || phase === 'ask') {
    // CBG-RC12-READ (frame d2-open): the public entry alone — the meeting, its day, the general
    // purpose — and the recorded step. Nothing of the minutes is read before "Open the minutes".
    const meeting = stepMeetingQuery.data;
    const description = [
      meeting?.title ?? null,
      meeting?.starts_at ? displayDate(meeting.starts_at) : null,
      (stepEntry?.purpose_label ?? '').trim() || w.noPurpose,
    ]
      .filter((part): part is string => part !== null && part.length > 0)
      .join(' · ');
    const backToMeeting = () =>
      void navigate({
        to: BOARD_DIRECTOR_PAGES.meeting,
        params: { meetingId: stepMeetingId ?? '' },
        search: orgRef === null ? undefined : directorBoardSearch(orgRef),
      });
    return (
      <DirectorShell
        title={w.stepTitle}
        // CBG-FIX34 (review-134's M1): a board this account no longer holds offers no way into
        // it, only the no-seat line's way back to the account's boards.
        description={notYours ? undefined : description}
        identity={identity}
        actions={notYours ? undefined : back}
        testId="page-director-record"
      >
        <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
          <Card testId="director-record-open-closed">
            <CardBody className="flex flex-col items-start gap-12">
              {phase === 'step' && stepEntry !== null ? (
                <>
                  <p className="font-sans text-s15 font-w600 leading-h15 tracking-givmo text-text-strong">
                    {steppedOutIsMine(stepEntry, board?.board_member_id ?? null)
                      ? w.youWerentInItSteppedOut
                      : w.youWerentInIt}
                  </p>
                  <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
                    {w.asADirector}
                  </p>
                </>
              ) : null}
              <div className="flex flex-wrap gap-10">
                {phase === 'step' ? (
                  <Button
                    appearance="saas"
                    tone="primary"
                    onClick={() => setOpenPressed(true)}
                    testId="director-record-open-minutes"
                  >
                    {w.openTheMinutes}
                  </Button>
                ) : (
                  // The window shut since the meeting page: whether these minutes were sent to this
                  // director is withheld until the confirmation, which the same words bring up.
                  <Button
                    appearance="saas"
                    tone="primary"
                    onClick={() => void opening.ask(stepSessionId ?? '')}
                    disabled={opening.asking !== null}
                    testId="director-record-open-ask"
                  >
                    {w.openItsMinutes}
                  </Button>
                )}
                <Button
                  appearance="saas"
                  tone="secondary"
                  onClick={backToMeeting}
                  testId="director-record-open-back"
                >
                  {w.backToTheMeeting}
                </Button>
              </div>
            </CardBody>
          </Card>
        </DirectorBoardContent>
        {opening.dialog}
      </DirectorShell>
    );
  }

  if (
    orgRef === null ||
    notYours ||
    phase === 'pending' ||
    recordQuery.isPending ||
    recordQuery.isError ||
    record === null
  ) {
    return (
      <DirectorShell
        // CBG-YR6-OUTSIDE (review-215's F5): never "your board" to a seat for someone who isn't on it.
        title={scope.committeeOnly ? co.recordPageTitle : 'A record of your board'}
        identity={identity}
        // CBG-FIX34 (review-134's M1): no way into a board this account no longer holds.
        actions={notYours ? undefined : back}
        testId="page-director-record"
      >
        <DirectorBoardContent orgRef={orgRef} notYours={notYours}>
          {recordQuery.isPending || phase === 'pending' ? (
            <Skeleton className="h-90 w-full rounded-lg" testId="director-record-loading" />
          ) : null}
          {/* ⚠ A refusal must NOT land in the error card below: the read gate opens the sheet and
              re-reads THIS record once the window is open. */}
          <DirectorReadRefusal reads={[recordQuery]} testId="director-record-read-step-up" />
          {orgRef !== null && recordQuery.isError && isAnotherAccountRefusal(recordQuery.error) ? (
            <Card testId="director-record-another-account">
              <CardBody>
                <DirectorMovePointer
                  orgRef={orgRef}
                  blocks="open"
                  testId="director-record-move-pointer"
                />
              </CardBody>
            </Card>
          ) : null}
          {recordQuery.isError &&
          !isSigningRefusal(recordQuery.error) &&
          !isAnotherAccountRefusal(recordQuery.error) ? (
            <DirectorReadFailed
              read={recordQuery}
              line="We couldn’t open this."
              testId="director-record-error"
              retryTestId="director-record-retry"
            />
          ) : null}
        </DirectorBoardContent>
      </DirectorShell>
    );
  }

  // No fingerprint means this is not the record yet, and there is nothing to name in an act.
  const contentHash = record.content_hash ?? '';
  // CBG-RC12-READ: the opening that stands for this read in the minutes' history, when it was one.
  const openedAt = sentenceMoment(record.opened_at);
  const canAcknowledge = contentHash.length > 0;
  // CBG-RC10: the open request asking this director, and the bytes this page rendered.
  const approval = directorApprovalRequest(record);
  const pendingHash = (record.pending_content_hash ?? '').trim();
  // CBG-FIX72: how and when it became the record, in the officer read's own sentence (the backend's
  // `approval_statement`, its day the UTC day); absent until it is the record.
  const approvalStatement = (record.approval_statement ?? '').trim() || null;

  const beginApprove = () => {
    if (pendingHash.length === 0 || orgRef === null) return;
    setApproveError(null);
    const path = { org_ref: orgRef, record_id: recordId };
    approveMinutes.begin(
      (intentStatementId) => () =>
        operations.directorSignBoardMinutesToApprove({
          path,
          // The echo names the exact bytes on this screen: the read's own fingerprint.
          body: { content_hash: pendingHash, intent_statement_id: intentStatementId },
          throwOnError: true,
        }),
    );
  };

  const beginAcknowledge = () => {
    if (!canAcknowledge || orgRef === null) return;
    setError(null);
    setStaleText(false);
    const path = { org_ref: orgRef, record_id: recordId };
    acknowledge.begin(
      (intentStatementId) => () =>
        operations.directorAcknowledgeBoardRecord({
          path,
          // The echo is what makes the act name the exact bytes on this screen.
          body: { content_hash: contentHash, intent_statement_id: intentStatementId },
          throwOnError: true,
        }),
    );
  };

  return (
    <DirectorShell
      title={record.title}
      description={
        <>
          {recordKindLabelOf(record)}
          {record.occurred_on ? ` · ${displayDate(record.occurred_on)}` : ''}
          {/* CBG-FIX140: what isn't the record says so, in the officer record page's word — a
              draft reads "Draft" (D-CBG-22: directors read drafts, marked, never hidden) — and the
              notice above its text says it whole (review-291's M1). The record itself says how it
              became one, in the approval line below. */}
          {notTheRecord(record) ? (
            <>
              {' · '}
              <StatusWord testId="director-record-state">{recordStandingLabel(record)}</StatusWord>
            </>
          ) : null}
        </>
      }
      identity={identity}
      actions={back}
      testId="page-director-record"
    >
      {openedAt !== null ? (
        // CBG-RC12-READ (frame d3-opened): this read opened minutes this director wasn't sent, and
        // their history says so — first, before anything the minutes hold.
        <Card testId="director-record-opened">
          <CardBody className="flex flex-col gap-6">
            <span className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {w.openedByYou(openedAt)}
            </span>
            <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
              {w.inTheirHistory}
            </span>
          </CardBody>
        </Card>
      ) : null}

      {approval !== null ? (
        // CBG-RC10 (frame d5): above the minutes, what the board's rule asks of this director.
        <Card testId="director-record-approval">
          <CardBody className="flex flex-col gap-12">
            <p
              className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid="director-record-approval-lead"
            >
              {rc10.directorLead(approval.signerCount, approval.signedCount)}
            </p>
            <p
              className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
              data-testid="director-record-approval-record-not-decisions"
            >
              {rc11.directorRecordNotDecisions}
            </p>
            {approval.sessionChanged ? (
              <p
                className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
                data-testid="director-record-approval-session-changed"
              >
                {rc10.directorSessionChanged}
              </p>
            ) : approval.youSigned === 'current' ? (
              <p
                className="font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong"
                data-testid="director-record-approval-signed"
              >
                {rc10.youSigned(sentenceMoment(approval.youSignedAt))}
              </p>
            ) : (
              <>
                {approval.youSigned === 'earlier_version' ? (
                  <p
                    className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
                    data-testid="director-record-approval-earlier"
                  >
                    {rc10.signTheCurrentVersion}
                  </p>
                ) : null}
                {pendingHash.length > 0 ? (
                  <DirectorActRow
                    state={approveMinutes}
                    label="Sign"
                    onPress={beginApprove}
                    error={approveError}
                    testIdPrefix="director-record-approval"
                    buttonTestId="director-record-approval-sign"
                  />
                ) : null}
              </>
            )}
          </CardBody>
        </Card>
      ) : signedLast ? (
        <p
          className="font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong"
          {...testIdProp('director-record-approval-approved')}
        >
          {rc10.youSignedLast}
        </p>
      ) : null}

      {approvalStatement !== null ? (
        // CBG-FIX72: how and when it became the record, above the text where the request to approve
        // it sat while that was open, in the officer read's own sentence.
        <Card testId="director-record-approved">
          <CardBody>
            <p
              className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid="director-record-approval-statement"
            >
              {approvalStatement}
            </p>
          </CardBody>
        </Card>
      ) : null}

      {notTheRecord(record) ? (
        // CBG-FIX140, review-291's M1: above the text, the book's own line, so a draft's minutes are
        // never read as the record. Said whole while it may still become the record; a discarded
        // draft never will, so its word stands alone.
        <Card testId="director-record-notice">
          <CardBody>
            <p className="font-sans text-s15 font-w600 leading-h15 tracking-givmo text-text-strong">
              {record.state === 'discarded'
                ? recordStandingLabel(record)
                : `${recordStandingLabel(record)} · ${dp.untilApproved}`}
            </p>
          </CardBody>
        </Card>
      ) : null}

      <Card testId="director-record-text">
        <CardBody className="flex flex-col gap-12">
          {record.structured_record ? (
            // Minutes carry two halves, as RC9's "Read the minutes" shows them (frame d3-opened):
            // the secretary's sections, and what was recorded about the meeting.
            <>
              <div className="flex flex-col gap-4">
                <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
                  {rc9.secretarysSections}
                </span>
                <pre
                  className="whitespace-pre-wrap font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary"
                  data-testid="director-record-body"
                >
                  {record.body}
                </pre>
              </div>
              <div className="flex flex-col gap-4" data-testid="director-record-structured">
                <span className="font-sans text-s12 font-w600 tracking-givmo text-text-secondary">
                  {rc9.whatWasRecorded}
                </span>
                <pre className="whitespace-pre-wrap [overflow-wrap:anywhere] font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
                  {record.structured_record}
                </pre>
              </div>
            </>
          ) : (
            <pre
              className="whitespace-pre-wrap font-sans text-s15 font-w400 leading-h15 tracking-givmo text-text-secondary"
              data-testid="director-record-body"
            >
              {record.body}
            </pre>
          )}
          {canAcknowledge ? (
            <div className="flex flex-col gap-6">
              <span className="font-sans text-s12 font-w400 leading-h15 tracking-givmo text-text-tertiary">
                Anything you record against this names this exact wording, by its fingerprint
              </span>
              {/* The WHOLE fingerprint (CBG-UX4): a shortened one cannot be compared. */}
              <Fingerprint value={contentHash} testId="director-record-hash" />
            </div>
          ) : null}
          {record.created_at ? (
            <span className="font-sans text-s12 font-w400 tracking-givmo text-text-tertiary">
              {/* A draft's row was made when its draft was started: "Entered" would read as its
                  entry in the book (review-291's M1). */}
              {notTheRecord(record)
                ? dp.draftStarted(displayInstant(record.created_at))
                : `Entered ${displayInstant(record.created_at)}`}
            </span>
          ) : null}
        </CardBody>
      </Card>

      {acknowledged ? (
        <p
          className="font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong"
          {...testIdProp('director-record-acknowledged')}
        >
          Thank you — it is recorded that you have read this.
        </p>
      ) : record.acknowledgment ? (
        // CBG-YR4-ACK (contract §2.4): this seat's acknowledgment of these bytes, from the read
        // itself — so a reload says so instead of offering the act again.
        <p
          className="font-sans text-s14 font-w600 leading-h15 tracking-givmo text-text-strong"
          {...testIdProp('director-record-confirmed')}
        >
          {record.acknowledgment.road === 'paper'
            ? gsWords.paperConfirmedOn(
                shortDay(
                  record.acknowledgment.paper_signed_on ?? record.acknowledgment.acknowledged_at,
                ),
              )
            : gsWords.youConfirmedOn(shortDay(record.acknowledgment.acknowledged_at))}
        </p>
      ) : null}

      {staleText ? (
        <ErrorMessage testId="director-record-stale">
          This changed while you had it open, so nothing was recorded against the old wording. Read
          it again above — what you see now is the current wording.
        </ErrorMessage>
      ) : null}

      {canAcknowledge && !acknowledged && !record.acknowledgment ? (
        <Card testId="director-record-actions">
          <CardBody className="flex flex-col gap-12">
            <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {fromList
                ? gsWords.askedToConfirm
                : isCommitteeOnly(board)
                  ? co.acknowledgeLine
                  : 'If your board asked you to confirm you have read this, you can record that here.'}
            </p>
            <DirectorActRow
              state={acknowledge}
              label="Record that I’ve read this"
              onPress={beginAcknowledge}
              error={error}
              testIdPrefix="director-record"
              buttonTestId="director-record-acknowledge"
            />
          </CardBody>
        </Card>
      ) : null}

      {/* A record open in one tab can have its window lapse while it is read: the gate answers that
          on THIS read and re-opens the same record. */}
      <DirectorReadRefusal reads={[recordQuery]} testId="director-record-read-step-up" />

      {acknowledge.dialog}
      {approveMinutes.dialog}
    </DirectorShell>
  );
}
